import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Clock, Play, History, Loader2, CheckCircle2, AlertTriangle,
  Calendar, Globe2, RefreshCw,
} from "lucide-react";

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const FREQ_LABELS = {
  hourly: "Hourly",
  daily: "Daily",
  weekly: "Weekly",
  fortnightly: "Fortnightly",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
  manual: "Manual only",
};

const CATEGORY_LABELS = {
  integration_sync: "Integration sync",
  report: "Report",
  maintenance: "Maintenance",
};

export default function SchedulerPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningId, setRunningId] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scheduler/jobs");
      setData(data);
    } catch (e) {
      toast.error("Failed to load scheduled jobs");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patchJob = async (jobId, patch) => {
    try {
      await api.patch(`/scheduler/jobs/${jobId}`, patch);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const runNow = async (jobId) => {
    setRunningId(jobId);
    try {
      const { data: r } = await api.post(`/scheduler/jobs/${jobId}/run`);
      if (r.status === "success") {
        const desc = Object.entries(r.output || {})
          .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
          .join(" · ");
        toast.success(`Ran in ${r.duration_ms}ms · ${desc || "ok"}`, { duration: 6000 });
      } else {
        toast.error(`Failed: ${r.error || "unknown error"}`, { duration: 8000 });
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally { setRunningId(null); }
  };

  const showHistory = async (job) => {
    setHistoryFor(job); setHistoryLoading(true); setHistory([]);
    try {
      const { data: r } = await api.get(`/scheduler/jobs/${job.job_id}/runs?limit=30`);
      setHistory(r.runs || []);
    } catch (e) {
      toast.error("Failed to load history");
    } finally { setHistoryLoading(false); }
  };

  if (loading) {
    return (
      <div className="p-8 text-sm text-zinc-500 flex items-center gap-2"
        data-testid="scheduler-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading scheduler…
      </div>
    );
  }
  if (!data) return null;

  const grouped = {};
  for (const j of data.jobs) {
    const k = j.category || "integration_sync";
    (grouped[k] = grouped[k] || []).push(j);
  }

  return (
    <div className="space-y-6" data-testid="scheduler-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-overline mb-2">Automation</div>
          <h1 className="font-display font-extrabold text-4xl tracking-tight">Scheduler</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Every periodic data pull and maintenance job. Change frequency or time-of-day,
            disable a job, or run it manually — settings persist and survive restarts.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="rounded-sm font-mono text-[11px]">
            <Globe2 className="h-3 w-3 mr-1" /> Timezone: {data.timezone}
          </Badge>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={load}
            data-testid="scheduler-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {Object.entries(grouped).map(([cat, jobs]) => (
        <Card key={cat} className="rounded-sm border-zinc-200 overflow-hidden">
          <CardContent className="p-0">
            <div className="px-5 py-3 bg-zinc-50/60 border-b border-zinc-100">
              <div className="label-overline">{CATEGORY_LABELS[cat] || cat}</div>
            </div>
            <div className="divide-y divide-zinc-100">
              {jobs.map((j) => (
                <JobRow key={j.job_id} job={j}
                  frequencies={data.allowed_frequencies}
                  onChange={(patch) => patchJob(j.job_id, patch)}
                  onRun={() => runNow(j.job_id)}
                  onHistory={() => showHistory(j)}
                  running={runningId === j.job_id} />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}

      <HistoryDialog open={!!historyFor} onClose={() => setHistoryFor(null)}
        job={historyFor} history={history} loading={historyLoading} />
    </div>
  );
}

function JobRow({ job, frequencies, onChange, onRun, onHistory, running }) {
  const freq = job.frequency || "daily";
  const needsTime = !["hourly", "manual"].includes(freq);
  const needsWeekday = ["weekly", "fortnightly"].includes(freq);
  const needsDayOfMonth = ["monthly", "quarterly", "yearly"].includes(freq);

  const last = job.last_run_at ? new Date(job.last_run_at) : null;
  const lastStatus = job.last_status;
  const next = job.next_run_at ? new Date(job.next_run_at) : null;

  return (
    <div className="px-5 py-4" data-testid={`scheduler-job-row-${job.job_id}`}>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-bold text-base">{job.name}</h3>
            <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
              {job.job_id}
            </Badge>
            {!job.enabled && (
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 text-[10px]">
                Disabled
              </Badge>
            )}
            {lastStatus === "success" && (
              <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Last: success
              </Badge>
            )}
            {lastStatus === "error" && (
              <Badge className="rounded-sm bg-red-100 text-red-800 border-red-200 text-[10px]"
                title={job.last_error || ""}>
                <AlertTriangle className="h-3 w-3 mr-1" /> Last: error
              </Badge>
            )}
          </div>
          <p className="text-sm text-zinc-500 mt-1">{job.description}</p>
          <div className="text-[11px] font-mono text-zinc-400 mt-1.5 flex gap-3 flex-wrap">
            {last && <span>last run: {last.toLocaleString()}</span>}
            {next && job.enabled && freq !== "manual" && (
              <span>next: {next.toLocaleString()}</span>
            )}
            {job.last_duration_ms != null && (
              <span>{job.last_duration_ms}ms</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" className="rounded-sm"
            onClick={onHistory}
            data-testid={`scheduler-history-${job.job_id}`}>
            <History className="h-3.5 w-3.5 mr-1.5" /> History
          </Button>
          <Button size="sm"
            className="rounded-sm bg-[#002FA7] hover:bg-[#00227A] text-white"
            onClick={onRun} disabled={running}
            data-testid={`scheduler-run-${job.job_id}`}>
            {running ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
            Run now
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 mt-4 items-end">
        <div>
          <Label className="label-overline mb-1.5">Frequency</Label>
          <Select value={freq}
            onValueChange={(v) => onChange({ frequency: v })}>
            <SelectTrigger className="rounded-sm" data-testid={`scheduler-freq-${job.job_id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {frequencies.map((f) => (
                <SelectItem key={f} value={f}>{FREQ_LABELS[f] || f}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {needsTime && (
          <div>
            <Label className="label-overline mb-1.5">
              Time of day ({DOW_LABELS[(new Date()).getDay() === 0 ? 6 : (new Date()).getDay() - 1]} {job.timezone})
            </Label>
            <div className="flex items-center gap-2">
              <Input type="number" min={0} max={23}
                value={job.hour}
                onChange={(e) => onChange({ hour: clamp(e.target.value, 0, 23) })}
                className="rounded-sm w-20 font-mono text-center"
                data-testid={`scheduler-hour-${job.job_id}`} />
              <span className="text-zinc-400 font-mono">:</span>
              <Input type="number" min={0} max={59}
                value={job.minute}
                onChange={(e) => onChange({ minute: clamp(e.target.value, 0, 59) })}
                className="rounded-sm w-20 font-mono text-center"
                data-testid={`scheduler-minute-${job.job_id}`} />
            </div>
          </div>
        )}

        {freq === "hourly" && (
          <div>
            <Label className="label-overline mb-1.5">Minute past hour</Label>
            <Input type="number" min={0} max={59} value={job.minute}
              onChange={(e) => onChange({ minute: clamp(e.target.value, 0, 59) })}
              className="rounded-sm w-20 font-mono text-center"
              data-testid={`scheduler-minute-${job.job_id}`} />
          </div>
        )}

        {needsWeekday && (
          <div>
            <Label className="label-overline mb-1.5">Day of week</Label>
            <Select value={String(job.day_of_week)}
              onValueChange={(v) => onChange({ day_of_week: Number(v) })}>
              <SelectTrigger className="rounded-sm" data-testid={`scheduler-dow-${job.job_id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOW_LABELS.map((l, i) => (
                  <SelectItem key={l} value={String(i)}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {needsDayOfMonth && (
          <div>
            <Label className="label-overline mb-1.5">Day of month</Label>
            <Input type="number" min={1} max={28} value={job.day_of_month}
              onChange={(e) => onChange({ day_of_month: clamp(e.target.value, 1, 28) })}
              className="rounded-sm w-20 font-mono text-center"
              data-testid={`scheduler-dom-${job.job_id}`} />
          </div>
        )}

        <div className="flex items-center gap-2">
          <Switch checked={!!job.enabled}
            onCheckedChange={(v) => onChange({ enabled: v })}
            data-testid={`scheduler-enabled-${job.job_id}`} />
          <span className="text-sm text-zinc-600">Enabled</span>
        </div>
      </div>
    </div>
  );
}

function clamp(v, min, max) {
  const n = Number(v);
  if (Number.isNaN(n)) return min;
  return Math.max(min, Math.min(max, Math.floor(n)));
}

function HistoryDialog({ open, onClose, job, history, loading }) {
  if (!job) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[85vh] overflow-hidden flex flex-col" data-testid="scheduler-history-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-xl tracking-tight">
            {job.name} — run history
          </DialogTitle>
          <div className="text-[11px] font-mono text-zinc-500 mt-1">
            {job.job_id}
          </div>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : history.length === 0 ? (
          <div className="py-10 text-center text-sm text-zinc-400">
            No runs yet. Click <strong>Run now</strong> or wait for the next scheduled fire.
          </div>
        ) : (
          <div className="overflow-y-auto pr-1 space-y-2">
            {history.map((r) => (
              <div key={r.run_id}
                className={`rounded-sm border p-3 ${r.status === "error" ? "border-red-200 bg-red-50/40" : r.status === "success" ? "border-emerald-200 bg-emerald-50/30" : "border-zinc-200"}`}
                data-testid={`scheduler-run-row-${r.run_id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.status === "success" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                    {r.status === "error" && <AlertTriangle className="h-4 w-4 text-red-600" />}
                    {r.status === "running" && <Loader2 className="h-4 w-4 animate-spin text-zinc-500" />}
                    <span className="font-semibold text-sm">{r.status.toUpperCase()}</span>
                    <Badge variant="outline" className="rounded-sm font-mono text-[10px]">
                      {r.triggered_by || "scheduler"}
                    </Badge>
                    {r.duration_ms != null && (
                      <span className="text-[11px] font-mono text-zinc-500">{r.duration_ms}ms</span>
                    )}
                  </div>
                  <span className="text-[11px] font-mono text-zinc-500">
                    {new Date(r.started_at).toLocaleString()}
                  </span>
                </div>
                {r.error && (
                  <pre className="mt-2 text-[11px] font-mono text-red-800 whitespace-pre-wrap">{r.error}</pre>
                )}
                {r.output && Object.keys(r.output).length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] font-mono text-zinc-700">
                    {Object.entries(r.output).map(([k, v]) => (
                      <div key={k}>
                        <span className="text-zinc-400">{k}:</span>{" "}
                        <span>{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
